import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ActivityIndicator } from 'react-native';
import { LoadingSpinner } from '../common/LoadingSpinner';
import FeatherIcon from 'react-native-vector-icons/Feather';
import apiClient, { homeworkAPI, assessmentAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import type { AssessmentResultRow } from '../../types';

const MAX_FILES = 10;
const MAX_FILE_MB = 10;

interface Props {
  homeworkId: string;
  /** Called after a successful hand-in, so the list behind can refresh its status badges. */
  onSubmitted?: () => void;
  /**
   * Which module set this work - it decides which endpoints to talk to.
   *
   * The two live in different tables, so the id is only meaningful to one of
   * them: posting an assessment id to /HomeworkSubmission/submit answers
   * "Homework not found." with the file lost, which is exactly what a parent
   * hit before this existed.
   */
  source?: 'homework' | 'assessment';

  /**
   * Whether this work is collected as an upload at all.
   *
   * False for a timed paper, which is sat in class - there is nothing to hand
   * in, so the uploader is replaced with a note rather than collecting work
   * nobody will ever look at.
   */
  collectsUpload?: boolean;

  /**
   * What the TEACHER attached when setting the work.
   *
   * Shown above the child's own hand-in, because it is what they need in order
   * to do the work - a homework whose whole content is a worksheet arrived as a
   * title and a due date until this was passed through.
   */
  teacherFiles?: { id: string; fileUrl: string; fileName: string }[];
}

/**
 * "Your work" - the hand-in half of a homework, WEB build only (it uses the
 * browser's file input). Shown inside the homework modal on the list screen
 * and on the detail screen, so a parent can submit from wherever they are
 * reading the assignment.
 *
 * The server is the authority on everything here: whose child this is, whether
 * the homework is published, lateness, and when marks become visible.
 */
export function HomeworkHandInSection({
  homeworkId,
  onSubmitted,
  source,
  collectsUpload = true,
  teacherFiles = [],
}: Props) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { selectedStudent } = useAuth();

  const [submission, setSubmission] = useState<any | null>(null);
  /**
   * The published result behind a graded hand-in: rank, class average, the
   * grade's description and the teacher's feedback. The hand-in row carries
   * only marks and a comment, so once the work is graded the result record is
   * read too and the two are shown as one.
   */
  const [result, setResult] = useState<AssessmentResultRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [downloadingUrl, setDownloadingUrl] = useState<string | null>(null);

  const isAssessment = source === 'assessment';

  const load = useCallback(async () => {
    // Nothing to look up for work that is not collected online.
    if (!homeworkId || !selectedStudent?.enrollmentId || !collectsUpload) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      // The id is only meaningful to the module that issued it, so the lookup
      // has to follow the row's own source.
      const { data } = isAssessment
        ? await assessmentAPI.getMyAssessmentSubmission(homeworkId, selectedStudent.enrollmentId)
        : await homeworkAPI.getMySubmission(homeworkId, selectedStudent.enrollmentId);
      // null just means nothing handed in yet.
      setSubmission(data ?? null);
      setNote(data?.submissionText ?? '');

      // Graded: fetch the full result. Best-effort - the marks are already on
      // the hand-in row, so a failure here only loses rank and description.
      if (isAssessment && data?.isGradePublished && selectedStudent?.id) {
        try {
          const res = await assessmentAPI.getStudentResults(selectedStudent.id);
          const rows: AssessmentResultRow[] = res.data?.data ?? res.data ?? [];
          setResult(rows.find((r) => r.assessmentId === homeworkId) ?? null);
        } catch (err) {
          console.error('Failed to load the published result:', err);
          setResult(null);
        }
      } else {
        setResult(null);
      }
    } catch (err) {
      console.error('Failed to load submission:', err);
    } finally {
      setLoading(false);
    }
  }, [homeworkId, selectedStudent?.enrollmentId, selectedStudent?.id, isAssessment, collectsUpload]);

  /** Graded work is closed: the result is shown in full and nothing more can be handed in. */
  const graded = !!submission?.isGradePublished;

  useEffect(() => { load(); }, [load]);

  const chooseFiles = () => {
    const docObj = (globalThis as any).document;
    if (!docObj?.createElement) return;
    const input = docObj.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = (e: any) => {
      const chosen: File[] = Array.from(e.target.files ?? []);
      setFeedback(null);
      setPendingFiles(prev => {
        const merged = [...prev];
        for (const f of chosen) {
          if (merged.length >= MAX_FILES) {
            setFeedback({ kind: 'error', text: `You can attach up to ${MAX_FILES} files.` });
            break;
          }
          if (f.size > MAX_FILE_MB * 1024 * 1024) {
            setFeedback({ kind: 'error', text: `${f.name} is over ${MAX_FILE_MB} MB.` });
            continue;
          }
          merged.push(f);
        }
        return merged;
      });
    };
    input.click();
  };

  const handleSubmit = async () => {
    if (!homeworkId || !selectedStudent?.enrollmentId) return;
    try {
      setSubmitting(true);
      setFeedback(null);
      const formData = new FormData();
      // The two endpoints name the id after their own table.
      formData.append(isAssessment ? 'AssessmentId' : 'HomeworkId', homeworkId);
      formData.append('EnrollmentId', selectedStudent.enrollmentId);
      if (note) formData.append('SubmissionText', note);
      // One 'Files' part per file - matches List<IFormFile> Files server-side.
      for (const f of pendingFiles) formData.append('Files', f);

      if (isAssessment) await assessmentAPI.submitAssessment(formData);
      else await homeworkAPI.submitHomework(formData);

      setPendingFiles([]);
      setFeedback({ kind: 'ok', text: 'Work submitted. Your teacher can see it now.' });
      await load();
      onSubmitted?.();
    } catch (err: any) {
      console.error('Homework submission failed:', err);
      setFeedback({
        kind: 'error',
        text: err?.response?.data?.message || 'Failed to submit homework. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const downloadFile = async (url: string, fallbackName: string) => {
    try {
      setDownloadingUrl(url);
      const { data } = await apiClient.get(url);
      if (!data?.imageBase64) throw new Error('Unexpected attachment payload');
      const docObj = (globalThis as any).document;
      const link = docObj.createElement('a');
      link.href = `data:${data.contentType || 'application/octet-stream'};base64,${data.imageBase64}`;
      link.download = data.fileName || fallbackName;
      docObj.body.appendChild(link);
      link.click();
      docObj.body.removeChild(link);
    } catch (err) {
      console.error('Failed to download file:', err);
      setFeedback({ kind: 'error', text: 'Unable to fetch the file at this time.' });
    } finally {
      setDownloadingUrl(null);
    }
  };

  /**
   * WHAT THE TEACHER GAVE THEM, above their own work.
   *
   * Rendered in both branches below: a paper that is not collected online still
   * has a worksheet or a revision list worth opening, and that is often the
   * entire content of the task.
   */
  const teacherFilesBlock = teacherFiles.length > 0 ? (
    <View style={styles.section}>
      <Text style={styles.sectionLabel}>From your teacher</Text>
      {teacherFiles.map((f) => (
        <TouchableOpacity
          key={f.id}
          style={styles.fileRow}
          onPress={() => downloadFile(f.fileUrl, f.fileName)}
          disabled={downloadingUrl === f.fileUrl}
        >
          <FeatherIcon name="file-text" size={15} color={colors.textSecondary} />
          <Text style={styles.fileName} numberOfLines={1}>{f.fileName}</Text>
          <FeatherIcon
            name={downloadingUrl === f.fileUrl ? 'loader' : 'download'}
            size={15}
            color={colors.primary}
          />
        </TouchableOpacity>
      ))}
    </View>
  ) : null;

  // A timed paper is sat in class: there is nothing to upload, and the server
  // refuses a hand-in against one anyway.
  if (!collectsUpload) {
    return (
      <>
      {teacherFilesBlock}
      <View style={styles.section}>
        <Text style={styles.sectionLabel}>Your work</Text>
        <Text style={styles.emptyText}>
          Not collected online - this one is taken in class, so there is nothing to
          upload. Marks and feedback appear under Assessments once the teacher
          publishes them.
        </Text>
      </View>
      </>
    );
  }

  return (
    <>
    {teacherFilesBlock}
    <View style={styles.section}>
      <Text style={styles.sectionLabel}>Your work</Text>

      {loading ? (
        <LoadingSpinner size="small" />
      ) : (
        <>
          {submission?.submittedDate ? (
            <View style={styles.metaRow}>
              <FeatherIcon
                name={submission.isLate ? 'alert-circle' : 'check-circle'}
                size={15}
                color={submission.isLate ? '#EF4444' : '#10B981'}
              />
              <Text style={styles.metaText}>
                {submission.isLate ? 'Handed in late' : 'Handed in'} on{' '}
                {new Date(submission.submittedDate).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric',
                })}
                {submission.attemptNumber > 1 ? ` (attempt ${submission.attemptNumber})` : ''}
              </Text>
            </View>
          ) : (
            <Text style={styles.emptyText}>Nothing handed in yet.</Text>
          )}

          {submission?.files?.length > 0 && submission.files.map((f: any) => (
            <TouchableOpacity
              key={f.id}
              style={styles.fileRow}
              onPress={() => downloadFile(f.fileUrl, f.fileName)}
              disabled={downloadingUrl === f.fileUrl}
            >
              <FeatherIcon name="file-text" size={15} color={colors.primary} />
              <Text style={styles.fileName} numberOfLines={1}>{f.fileName}</Text>
              {downloadingUrl === f.fileUrl
                ? <ActivityIndicator size="small" color={colors.textMuted} />
                : <FeatherIcon name="download" size={15} color={colors.textMuted} />}
            </TouchableOpacity>
          ))}

          {/* Marks appear only after the school publishes them; the server
              withholds them too, so this is presentation, not the gate. Once
              they are out this is the whole of the section: the result in full,
              and no way to hand in over graded work. */}
          {graded && (() => {
            const marks = result?.marksObtained ?? submission.receivedMarks;
            const outOf = result?.maxMarks ?? submission.outOfMarks;
            const grade = result?.grade ?? submission.grade;
            const percent = result?.percentage
              ?? (marks != null && outOf ? Math.round((marks / outOf) * 1000) / 10 : null);
            const teacherSays = result?.teacherFeedback || submission.teacherComments || null;
            const remarks = result?.remarks && result.remarks !== teacherSays ? result.remarks : null;
            const showStats = percent != null || result?.rank != null || result?.classAverage != null;
            return (
              <View style={styles.gradeBox}>
                <View style={styles.gradeHead}>
                  <FeatherIcon name="award" size={16} color="#166534" />
                  <Text style={styles.gradeLabel}>Graded</Text>
                </View>
                <Text style={styles.gradeValue}>
                  {marks} / {outOf}
                  {grade ? `  ${grade}` : ''}
                </Text>
                {!!result?.gradeDescription && (
                  <Text style={styles.gradeDesc}>{result.gradeDescription}</Text>
                )}

                {showStats && (
                  <View style={styles.gradeStats}>
                    {percent != null && (
                      <View style={styles.gradeStat}>
                        <Text style={styles.gradeStatLabel}>Score</Text>
                        <Text style={styles.gradeStatValue}>{percent}%</Text>
                      </View>
                    )}
                    {result?.rank != null && (
                      <View style={styles.gradeStat}>
                        <Text style={styles.gradeStatLabel}>Rank</Text>
                        <Text style={styles.gradeStatValue}>
                          #{result.rank}{result.classSize ? ` of ${result.classSize}` : ''}
                        </Text>
                      </View>
                    )}
                    {result?.classAverage != null && (
                      <View style={styles.gradeStat}>
                        <Text style={styles.gradeStatLabel}>Class average</Text>
                        <Text style={styles.gradeStatValue}>{result.classAverage}%</Text>
                      </View>
                    )}
                  </View>
                )}

                {!!teacherSays && (
                  <>
                    <Text style={styles.gradeSectionLabel}>Teacher feedback</Text>
                    <Text style={styles.gradeComment}>{teacherSays}</Text>
                  </>
                )}
                {!!remarks && (
                  <>
                    <Text style={styles.gradeSectionLabel}>Remarks</Text>
                    <Text style={styles.gradeComment}>{remarks}</Text>
                  </>
                )}
              </View>
            );
          })()}

          {!graded && (
          <>
          <TextInput
            style={styles.noteInput}
            placeholder="Add a note for your teacher (optional)"
            placeholderTextColor={colors.textMuted}
            value={note}
            onChangeText={setNote}
            multiline
          />

          {pendingFiles.map((f, i) => (
            <View key={`${i}-${f.name}`} style={styles.fileRow}>
              <FeatherIcon name="paperclip" size={14} color={colors.textPrimary} />
              <Text style={styles.fileName} numberOfLines={1}>{f.name}</Text>
              <TouchableOpacity
                onPress={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <FeatherIcon name="x" size={16} color={colors.textMuted} />
              </TouchableOpacity>
            </View>
          ))}

          <TouchableOpacity style={styles.attachButton} onPress={chooseFiles}>
            <FeatherIcon name="paperclip" size={15} color={colors.primary} />
            <Text style={styles.attachText}>
              {pendingFiles.length > 0 ? 'Add more files' : 'Attach files'}
            </Text>
          </TouchableOpacity>

          {submission?.files?.length > 0 && pendingFiles.length > 0 && (
            <Text style={styles.replaceHint}>
              Handing in again replaces the files submitted earlier.
            </Text>
          )}

          {feedback && (
            <Text style={feedback.kind === 'ok' ? styles.feedbackOk : styles.feedbackError}>
              {feedback.text}
            </Text>
          )}

          <TouchableOpacity
            style={[styles.submitButton, submitting && { opacity: 0.6 }]}
            onPress={handleSubmit}
            disabled={submitting}
          >
            {submitting
              ? <ActivityIndicator size="small" color="#FFF" />
              : (
                <Text style={styles.submitText}>
                  {submission?.submittedDate ? 'Hand in again' : 'Hand in'}
                </Text>
              )}
          </TouchableOpacity>
          </>
          )}
        </>
      )}
    </View>
    </>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  section: {
    marginBottom: 20,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: 16,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.textMuted,
    marginBottom: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 } as any,
  metaText: { fontSize: 13, color: colors.textSecondary, fontWeight: '500' },
  emptyText: { fontSize: 13, color: colors.textSecondary },
  fileRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 9,
    marginTop: 8,
    backgroundColor: colors.surface,
  } as any,
  fileName: { flex: 1, fontSize: 13, color: colors.textPrimary, fontWeight: '600' },
  gradeBox: { backgroundColor: '#DCFCE7', borderRadius: 10, padding: 12, marginTop: 12 },
  gradeHead: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 } as any,
  gradeLabel: { fontSize: 11, fontWeight: '700', color: '#166534', textTransform: 'uppercase', letterSpacing: 0.5 } as any,
  gradeValue: { fontSize: 18, fontWeight: '800', color: '#166534' },
  gradeDesc: { fontSize: 13, fontWeight: '600', color: '#15803D', marginTop: 2 },
  gradeStats: { flexDirection: 'row', flexWrap: 'wrap', gap: 14, marginTop: 10 } as any,
  gradeStat: { minWidth: 80 },
  gradeStatLabel: { fontSize: 11, color: '#15803D', fontWeight: '600' },
  gradeStatValue: { fontSize: 14, fontWeight: '800', color: '#166534', marginTop: 1 },
  gradeSectionLabel: { fontSize: 11, fontWeight: '700', color: '#15803D', marginTop: 10, textTransform: 'uppercase', letterSpacing: 0.5 } as any,
  gradeComment: { fontSize: 13, color: '#166534', marginTop: 4 },
  noteInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    padding: 10,
    minHeight: 60,
    marginTop: 12,
    color: colors.textPrimary,
    fontSize: 13,
  },
  attachButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: colors.primary,
    borderStyle: 'dashed',
    borderRadius: 10,
    paddingVertical: 10,
    marginTop: 10,
  } as any,
  attachText: { fontSize: 13, fontWeight: '700', color: colors.primary },
  replaceHint: { fontSize: 12, color: colors.textMuted, marginTop: 8 },
  feedbackOk: { fontSize: 13, color: '#10B981', marginTop: 10, fontWeight: '600' },
  feedbackError: { fontSize: 13, color: '#EF4444', marginTop: 10, fontWeight: '600' },
  submitButton: {
    backgroundColor: colors.primary,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center',
    marginTop: 12,
  },
  submitText: { color: '#FFFFFF', fontSize: 14, fontWeight: '700' },
});
